import { useRouter } from 'next/router'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { TimeLineContext } from '../../contexts'
import { useBigBrother } from '../../contexts/providers/BigBrotherProvider'
import { IPage, IPageSEO, ITimeLine, ITimeLineElement } from '../../types'
import { PlayerProvider } from '../GridBlocks/Reels/PlayerContext'
import { PlayerView } from '../GridBlocks/Reels/PlayerView'
import { SortByDropDown } from './SortByDropDown'
import { StyledColumn, StyledRow, WrapperFlex } from './TimeLine.styles'
import { TimeLineSubItem } from './TimeLineSubItem'
import { TimeLineTags } from './TimeLineTags'

type TimeLineProps = {
  data: IPage
}

const TimeLine: React.FC<TimeLineProps> = props => {
  const dta = props.data.Content.Item?.timeLines || []
  const data_key = Object.keys(dta)[0] as string
  const timeLineArr = (dta[
    data_key as keyof typeof dta
  ] as unknown as ITimeLine) || { elements: [] }

  const [filterElements, setFilterElements] = useState<Array<ITimeLineElement>>(
    timeLineArr.elements
  )
  const [activeTag, setActiveTag] = useState('All')
  const refElements = useRef<Array<ITimeLineElement>>([])
  const timeLineCtx = useContext(TimeLineContext)
  const { showPlayer, handleClosePlayer, videos } = useBigBrother()
  const router = useRouter()
  const isBigBrother = router.asPath.includes('the-big-brother')

  useEffect(() => {
    timeLineCtx.setElements(refElements.current)
  }, [props, timeLineCtx])

  // Lock body scroll when the modal is open
  useEffect(() => {
    if (showPlayer) {
      document.body.style.overflow = 'hidden' // Disable scrolling
    } else {
      document.body.style.overflow = 'auto' // Enable scrolling when modal is closed
    }

    // Clean up on unmount
    return () => {
      document.body.style.overflow = 'auto' // Ensure scrolling is re-enabled
    }
  }, [showPlayer])

  const renderItem = (SEO: IPageSEO) => {
    if (refElements.current) {
      return (
        filterElements.length > 0 &&
        Object.entries(filterElements).map((el, index, elements) => {
          const isLast = index + 1 === elements.length
          const props = {
            key: el[0],
            id: el[1].id + 'Item',
            data: el[1],
          }
          const adIndex = index + 1
          const bulletColor = dta[
            Object.keys(dta)[0] as string as keyof typeof dta
          ] as unknown as ITimeLine

          const orderClassName =
            index === 0
              ? 'first'
              : index + 1 === filterElements.length
              ? 'last'
              : 'middle'

          return (
            <TimeLineSubItem
              key={'TimeLineSubItem' + index}
              el={el}
              index={index}
              adIndex={adIndex}
              orderClassName={orderClassName}
              isLast={isLast}
              SEO={SEO}
              data={props}
              color={bulletColor.header.favcolor}
            />
          )
        })
      )
    }
    return null
  }

  const tagFilterHandler = (id: string) => {
    let filteredElements = []
    if (id === 'All') {
      filteredElements = refElements.current
    } else {
      filteredElements = refElements.current.filter(el => {
        return el.tags.find(tag => {
          return tag.term_id === id
        })
      })
    }
    setActiveTag(id)
    setFilterElements(filteredElements)
    timeLineCtx.setElements(filteredElements)
  }

  const sortElementsByDateHandler = (sotBy: string) => {
    const sortedElements =
      sotBy === 'newFirst' ? filterElements : [...filterElements.reverse()]
    setFilterElements(sortedElements)
    timeLineCtx.setElements(sortedElements)
  }

  //get data
  const data = props?.data.Content?.Item?.timeLines
  if (!data) {
    return null
  }
  const { SEO } = props.data
  const key = Object.keys(data)[0] as string
  const timeLineData = data[key as keyof typeof data] as unknown as ITimeLine
  if (timeLineData) {
    refElements.current = timeLineData.elements
    return (
      <>
        <StyledRow key={'tl_wrapper_' + key}>
          <StyledColumn>
            <WrapperFlex>
              <SortByDropDown
                sortElementsBysoDate={sortElementsByDateHandler}
              />
              <TimeLineTags
                timeline={timeLineData}
                tagFilterHandler={tagFilterHandler}
                activeTag={activeTag}
              />
            </WrapperFlex>
            {renderItem(SEO)}
          </StyledColumn>
        </StyledRow>
        {isBigBrother && showPlayer && (
          <PlayerProvider isMiniSeries={false}>
            <PlayerView
              showPlayer={showPlayer}
              closePlayer={handleClosePlayer}
              updatedPost={videos}
              stagger={0}
              loop={false}
              seekBarColor='#1FB5F9'
              playerStyle={{
                borderCorner: 'rounded',
                seekBar: 'stacked',
                overlayPadding: 20,
                overlayPaddingMobile: 12,
                seekBarTimeSize: 14,
                overlayInfoPaddingBottom: 20,
                overlayInfoPaddingBottomMobile: 12,
                overlayInfoGap: 10,
              }}
            />
          </PlayerProvider>
        )}
      </>
    )
  }
  return null
}
export default TimeLine
